import ks from "../ReUsableTS/kBrowserShared";
import { kStyles } from "../ReUsableTS/kStyles";
import LNG from "../ReUsableTS/kLNG";
import { kPosition } from "../ReUsableTS/kHTML";
import { kContextMenuColorSwatch } from "../ReUsableTS/kContextMenu";
import { kEditableTable, kEditableColumn, kHTMLEditableColType } from "../ReUsableTS/kEditableTable";
import { kColumnFilter, kFilterOperator } from "../ReUsableTS/kEditableTableFilter";

// One checklist row: a checkbox + (optional colour swatch) + label. Held as an
// object so the search box can show/hide it and Apply can read its state
// without per-row closures.
interface kChecklistEntry {
    Input: HTMLInputElement;
    Row: HTMLLabelElement;
    Text: string;
}

// The Tier-1 non-modal, anchored filter popup for a SINGLE column. Shows an
// operator + value section for text/number/date columns and an Excel-style
// distinct-value checklist for list-like columns (text shows both). Reads and
// writes the grid's FilterModel; the grid re-applies on Apply/Clear. ONE
// concern: edit one column's condition. Closes on outside-click or Escape.
export class kColumnFilterPopup {
    public Grid: kEditableTable | null = null;
    public Column: kEditableColumn | null = null;
    public Anchor: HTMLElement | null = null;
    public Popup: HTMLDivElement | null = null;
    public OperatorSelect: HTMLSelectElement | null = null;
    public ValueInput: HTMLInputElement | null = null;
    public Value2Input: HTMLInputElement | null = null;
    public ValueRow: HTMLDivElement | null = null;
    public Value2Row: HTMLDivElement | null = null;
    public SearchBox: HTMLInputElement | null = null;
    public Entries: kChecklistEntry[] = [];
    private _OutsideHandler: ((Ev: Event) => void) | null = null;
    private _KeyHandler: ((Ev: KeyboardEvent) => void) | null = null;
    private _PreviouslyFocused: HTMLElement | null = null;

    public Open = (Grid: kEditableTable, Column: kEditableColumn, Anchor: HTMLElement): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Close();
            this.Grid = Grid;
            this.Column = Column;
            this.Anchor = Anchor;
            // Remember focus so Close can restore it (keyboard/screen-reader users
            // return to the funnel they opened, mirroring kModalDialog).
            this._PreviouslyFocused = document.activeElement as HTMLElement;
            this.Entries = [];
            this._Build();
            this._Prefill();
            this._Position();
            this._WireDismissal();
            if (this.SearchBox !== null) { this.SearchBox.focus(); }
            else if (this.OperatorSelect !== null) { this.OperatorSelect.focus(); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup.Open");
        }
    };

    public Close = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const HadPopup: boolean = this.Popup !== null;
            if (this._OutsideHandler !== null) {
                window.removeEventListener("mousedown", this._OutsideHandler, true);
                this._OutsideHandler = null;
            }
            if (this._KeyHandler !== null) {
                window.removeEventListener("keydown", this._KeyHandler, true);
                this._KeyHandler = null;
            }
            if (this.Popup !== null && this.Popup.parentNode !== null) {
                this.Popup.parentNode.removeChild(this.Popup);
            }
            this.Popup = null;
            // Restore focus only on a genuine dismissal (a popup was open), so the
            // pre-build Close() inside Open never steals focus from the new popup.
            if (HadPopup === true && this._PreviouslyFocused !== null) {
                this._PreviouslyFocused.focus();
            }
            this._PreviouslyFocused = null;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup.Close");
        }
    };

    private _Build = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            kColumnFilterPopup.AddStyles();
            const Popup: HTMLDivElement = document.createElement("div");
            Popup.className = "kFilterPopup";
            Popup.setAttribute("role", "dialog");
            Popup.setAttribute("aria-labelledby", "kFilterPopupTitle");
            this.Popup = Popup;
            this._BuildTitle(Popup);
            if (this.Column !== null && kColumnFilterPopup.UsesOperator(this.Column.Type) === true) {
                this._BuildOperatorSection(Popup);
            }
            if (this.Column !== null && kColumnFilterPopup.UsesChecklist(this.Column.Type) === true) {
                this._BuildChecklistSection(Popup);
            }
            this._BuildButtons(Popup);
            document.body.appendChild(Popup);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._Build");
        }
    };

    private _BuildTitle = (Popup: HTMLDivElement): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Column === null) { return; }
            const Title: HTMLDivElement = document.createElement("div");
            Title.className = "kFilterPopupTitle";
            // id matches the popup's aria-labelledby; only one popup exists at a
            // time (the prior one is removed before a new build), so it is unique.
            Title.id = "kFilterPopupTitle";
            Title.textContent = LNG.FILTERHEADING + ": " + this.Column.Title;
            Popup.appendChild(Title);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._BuildTitle");
        }
    };

    private _BuildOperatorSection = (Popup: HTMLDivElement): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Column === null) { return; }
            const Select: HTMLSelectElement = document.createElement("select");
            Select.className = "kFilterOperator";
            const Operators: kFilterOperator[] = kColumnFilter.OperatorsFor(this.Column.Type);
            let CurrOp: kFilterOperator;
            for (CurrOp of Operators) {
                const Option: HTMLOptionElement = document.createElement("option");
                Option.value = CurrOp.toString();
                Option.textContent = kColumnFilterPopup.OperatorLabel(CurrOp);
                Select.appendChild(Option);
            }
            Select.addEventListener("change", this._OnOperatorChange);
            Popup.appendChild(Select);
            this.OperatorSelect = Select;
            this._BuildValueInputs(Popup);
            this._SyncValueVisibility();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._BuildOperatorSection");
        }
    };

    private _BuildValueInputs = (Popup: HTMLDivElement): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Column === null) { return; }
            const InputType: string = kColumnFilterPopup.ValueInputType(this.Column.Type);
            this.ValueRow = document.createElement("div");
            this.ValueRow.className = "kFilterValueRow";
            this.ValueInput = document.createElement("input");
            this.ValueInput.type = InputType;
            this.ValueInput.className = "kFilterValue";
            this.ValueInput.placeholder = LNG.FILTERVALUELABEL;
            this.ValueInput.addEventListener("keydown", this._OnValueKeyDown);
            this.ValueRow.appendChild(this.ValueInput);
            Popup.appendChild(this.ValueRow);
            this.Value2Row = document.createElement("div");
            this.Value2Row.className = "kFilterValueRow";
            this.Value2Input = document.createElement("input");
            this.Value2Input.type = InputType;
            this.Value2Input.className = "kFilterValue";
            this.Value2Input.addEventListener("keydown", this._OnValueKeyDown);
            this.Value2Row.appendChild(this.Value2Input);
            Popup.appendChild(this.Value2Row);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._BuildValueInputs");
        }
    };

    private _BuildChecklistSection = (Popup: HTMLDivElement): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Grid === null || this.Column === null) { return; }
            const Search: HTMLInputElement = document.createElement("input");
            Search.type = "search";
            Search.className = "kFilterValuesSearch";
            Search.placeholder = LNG.FILTERSEARCHVALUES;
            Search.addEventListener("input", this._OnSearchValues);
            Popup.appendChild(Search);
            this.SearchBox = Search;
            this._BuildSelectAll(Popup);
            const List: HTMLDivElement = document.createElement("div");
            List.className = "kFilterChecklist";
            const Values: string[] = this.Grid.FilterModel.DistinctValues(this.Grid.Rows.Items, this.Column);
            let CurrValue: string;
            for (CurrValue of Values) { this._AppendChecklistRow(List, CurrValue); }
            Popup.appendChild(List);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._BuildChecklistSection");
        }
    };

    private _BuildSelectAll = (Popup: HTMLDivElement): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Row: HTMLLabelElement = document.createElement("label");
            Row.className = "kFilterChecklistRow kFilterSelectAll";
            const Box: HTMLInputElement = document.createElement("input");
            Box.type = "checkbox";
            Box.className = "kFilterSelectAllBox";
            Box.addEventListener("change", this._OnSelectAll);
            const Text: HTMLSpanElement = document.createElement("span");
            Text.textContent = LNG.FILTERSELECTALL;
            Row.appendChild(Box);
            Row.appendChild(Text);
            Popup.appendChild(Row);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._BuildSelectAll");
        }
    };

    private _AppendChecklistRow = (List: HTMLDivElement, Value: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Column === null) { return; }
            const Row: HTMLLabelElement = document.createElement("label");
            Row.className = "kFilterChecklistRow";
            const Box: HTMLInputElement = document.createElement("input");
            Box.type = "checkbox";
            Box.value = Value;
            Row.appendChild(Box);
            if (this.Column.Type === kHTMLEditableColType.Color) {
                // Reuse the shared colour-swatch renderer (handles the empty /
                // "no colour" case too). Its own click is a no-op (ClickCB left
                // null), so clicking it still toggles the row's checkbox via the
                // wrapping <label>.
                const Swatch: kContextMenuColorSwatch = new kContextMenuColorSwatch();
                Swatch.Color = Value;
                Swatch.Label = Value;
                Row.appendChild(Swatch.ToDiv());
            } else {
                const Text: HTMLSpanElement = document.createElement("span");
                Text.textContent = Value;
                Row.appendChild(Text);
            }
            List.appendChild(Row);
            this.Entries.push({ Input: Box, Row: Row, Text: Value.toLowerCase() });
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._AppendChecklistRow");
        }
    };

    private _BuildButtons = (Popup: HTMLDivElement): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Row: HTMLDivElement = document.createElement("div");
            Row.className = "kFilterButtons";
            const Clear: HTMLButtonElement = document.createElement("button");
            Clear.type = "button";
            Clear.className = "kFilterBtn kFilterClearBtn";
            Clear.textContent = LNG.FILTERCLEAR;
            Clear.addEventListener("click", this._OnClear);
            const Apply: HTMLButtonElement = document.createElement("button");
            Apply.type = "button";
            Apply.className = "kFilterBtn kFilterApplyBtn";
            Apply.textContent = LNG.FILTERAPPLY;
            Apply.addEventListener("click", this._OnApply);
            Row.appendChild(Clear);
            Row.appendChild(Apply);
            Popup.appendChild(Row);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._BuildButtons");
        }
    };

    // Re-populate the controls from the column's existing condition so reopening
    // the popup shows the active filter rather than a blank form.
    private _Prefill = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Grid === null || this.Column === null) { return; }
            const Existing: kColumnFilter | null = this.Grid.FilterModel.GetColumnFilter(this.Column.KObjectPropertyName);
            if (Existing === null) { return; }
            if (this.OperatorSelect !== null && Existing.Operator !== kFilterOperator.InList) {
                this.OperatorSelect.value = Existing.Operator.toString();
                if (this.ValueInput !== null) { this.ValueInput.value = Existing.Value; }
                if (this.Value2Input !== null) { this.Value2Input.value = Existing.Value2; }
                this._SyncValueVisibility();
            }
            this._PrefillChecklist(Existing);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._Prefill");
        }
    };

    private _PrefillChecklist = (Existing: kColumnFilter): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Existing.Operator !== kFilterOperator.InList) { return; }
            const Checked: string[] = [];
            let CurrSel: string;
            for (CurrSel of Existing.SelectedValues) { Checked.push(CurrSel.toLowerCase()); }
            let CurrEntry: kChecklistEntry;
            for (CurrEntry of this.Entries) {
                if (Checked.indexOf(CurrEntry.Text) >= 0) { CurrEntry.Input.checked = true; }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._PrefillChecklist");
        }
    };

    private _OnApply = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Grid === null || this.Column === null) { return; }
            const Filter: kColumnFilter = this._BuildFilterFromInputs();
            if (Filter.IsEmptyCondition() === true) {
                this.Grid.ClearColumnFilter(this.Column.KObjectPropertyName);
            } else {
                this.Grid.FilterModel.SetColumnFilter(Filter);
                this.Grid.ApplyFilters();
            }
            this.Close();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._OnApply");
        }
    };

    // Precedence: an explicit operator + value wins; otherwise the checklist
    // selection becomes an InList condition. This keeps exactly one condition
    // per column while letting text columns offer both UIs.
    private _BuildFilterFromInputs = (): kColumnFilter => {
        if (ks.KErrorMode === true) { return new kColumnFilter(); }
        try {
            const Filter: kColumnFilter = new kColumnFilter();
            if (this.Column === null) { return Filter; }
            Filter.PropName = this.Column.KObjectPropertyName;
            Filter.ColType = this.Column.Type;
            Filter.Title = this.Column.Title;
            if (this._HasOperatorCondition() === true && this.OperatorSelect !== null) {
                Filter.Operator = ks.N0(this.OperatorSelect.value);
                if (this.ValueInput !== null) { Filter.Value = this.ValueInput.value; }
                if (this.Value2Input !== null) { Filter.Value2 = this.Value2Input.value; }
                return Filter;
            }
            Filter.Operator = kFilterOperator.InList;
            Filter.SelectedValues = this._CheckedValues();
            return Filter;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._BuildFilterFromInputs");
            return new kColumnFilter();
        }
    };

    private _HasOperatorCondition = (): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (this.OperatorSelect === null) { return false; }
            const Operator: kFilterOperator = ks.N0(this.OperatorSelect.value);
            if (kColumnFilterPopup.IsUnaryOperator(Operator) === true) { return true; }
            if (this.ValueInput !== null && ks.Nz(this.ValueInput.value).trim() !== "") { return true; }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._HasOperatorCondition");
            return false;
        }
    };

    private _CheckedValues = (): string[] => {
        if (ks.KErrorMode === true) { return []; }
        try {
            const Out: string[] = [];
            let CurrEntry: kChecklistEntry;
            for (CurrEntry of this.Entries) {
                if (CurrEntry.Input.checked === true) { Out.push(CurrEntry.Input.value); }
            }
            return Out;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._CheckedValues");
            return [];
        }
    };

    private _OnClear = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Grid === null || this.Column === null) { return; }
            this.Grid.ClearColumnFilter(this.Column.KObjectPropertyName);
            this.Close();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._OnClear");
        }
    };

    private _OnSelectAll = (Ev: Event): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Box: HTMLInputElement = Ev.target as HTMLInputElement;
            let CurrEntry: kChecklistEntry;
            for (CurrEntry of this.Entries) {
                if (CurrEntry.Row.style.display !== "none") { CurrEntry.Input.checked = Box.checked; }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._OnSelectAll");
        }
    };

    private _OnSearchValues = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.SearchBox === null) { return; }
            const Needle: string = this.SearchBox.value.trim().toLowerCase();
            let CurrEntry: kChecklistEntry;
            for (CurrEntry of this.Entries) {
                if (Needle === "" || CurrEntry.Text.indexOf(Needle) >= 0) {
                    CurrEntry.Row.style.display = "flex";
                } else {
                    CurrEntry.Row.style.display = "none";
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._OnSearchValues");
        }
    };

    private _OnOperatorChange = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this._SyncValueVisibility();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._OnOperatorChange");
        }
    };

    // Hide the value box(es) for unary operators (is empty / yes / no) and show
    // the second box only for Between.
    private _SyncValueVisibility = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.OperatorSelect === null || this.ValueRow === null || this.Value2Row === null) { return; }
            const Operator: kFilterOperator = ks.N0(this.OperatorSelect.value);
            if (kColumnFilterPopup.IsUnaryOperator(Operator) === true) {
                this.ValueRow.style.display = "none";
                this.Value2Row.style.display = "none";
                return;
            }
            this.ValueRow.style.display = "block";
            if (Operator === kFilterOperator.Between) { this.Value2Row.style.display = "block"; }
            else { this.Value2Row.style.display = "none"; }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._SyncValueVisibility");
        }
    };

    private _OnValueKeyDown = (Ev: KeyboardEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Ev.key === "Enter") { Ev.preventDefault(); this._OnApply(); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._OnValueKeyDown");
        }
    };

    private _Position = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Popup === null || this.Anchor === null) { return; }
            const Pos: kPosition = new kPosition();
            Pos.FromElement(this.Anchor);
            let Left: number = Pos.Left;
            const MaxLeft: number = window.scrollX + document.documentElement.clientWidth - 280;
            if (Left > MaxLeft) { Left = MaxLeft; }
            if (Left < 0) { Left = 0; }
            this.Popup.style.position = "absolute";
            this.Popup.style.top = (Pos.Top + this.Anchor.offsetHeight + 2).toString() + "px";
            this.Popup.style.left = Left.toString() + "px";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._Position");
        }
    };

    private _WireDismissal = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this._OutsideHandler = this._OnOutsideClick;
            this._KeyHandler = this._OnKeyDown;
            window.addEventListener("mousedown", this._OutsideHandler, true);
            window.addEventListener("keydown", this._KeyHandler, true);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._WireDismissal");
        }
    };

    private _OnOutsideClick = (Ev: Event): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Target: Node | null = Ev.target as Node;
            if (this.Popup !== null && Target !== null && this.Popup.contains(Target) === true) { return; }
            if (this.Anchor !== null && Target !== null && this.Anchor.contains(Target) === true) { return; }
            this.Close();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._OnOutsideClick");
        }
    };

    private _OnKeyDown = (Ev: KeyboardEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Ev.key === "Escape") { Ev.preventDefault(); Ev.stopPropagation(); this.Close(); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._OnKeyDown");
        }
    };

    public static IsUnaryOperator = (Operator: kFilterOperator): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Operator === kFilterOperator.IsEmpty) { return true; }
            if (Operator === kFilterOperator.IsNotEmpty) { return true; }
            if (Operator === kFilterOperator.IsTrue) { return true; }
            if (Operator === kFilterOperator.IsFalse) { return true; }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup.IsUnaryOperator");
            return false;
        }
    };

    public static UsesOperator = (ColType: kHTMLEditableColType): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (ColType === kHTMLEditableColType.Number) { return true; }
            if (ColType === kHTMLEditableColType.Currency) { return true; }
            if (ColType === kHTMLEditableColType.Date) { return true; }
            if (ColType === kHTMLEditableColType.DateTime) { return true; }
            if (ColType === kHTMLEditableColType.Time) { return true; }
            if (ColType === kHTMLEditableColType.Checkbox) { return true; }
            if (ColType === kHTMLEditableColType.Radio) { return true; }
            if (ColType === kHTMLEditableColType.Text) { return true; }
            if (ColType === kHTMLEditableColType.RichHtml) { return true; }
            // Hyperlink cells match on their Title/Url text (via ValueToText), so a
            // "contains"/"equals" operator is more useful than a checklist of mostly
            // unique link titles.
            if (ColType === kHTMLEditableColType.Link) { return true; }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup.UsesOperator");
            return false;
        }
    };

    public static UsesChecklist = (ColType: kHTMLEditableColType): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (ColType === kHTMLEditableColType.Select) { return true; }
            if (ColType === kHTMLEditableColType.SelectMulti) { return true; }
            if (ColType === kHTMLEditableColType.Color) { return true; }
            if (ColType === kHTMLEditableColType.Link) { return true; }
            if (ColType === kHTMLEditableColType.File) { return true; }
            if (ColType === kHTMLEditableColType.Image) { return true; }
            if (ColType === kHTMLEditableColType.Text) { return true; }
            if (ColType === kHTMLEditableColType.RichHtml) { return true; }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup.UsesChecklist");
            return false;
        }
    };

    public static ValueInputType = (ColType: kHTMLEditableColType): string => {
        if (ks.KErrorMode === true) { return "text"; }
        try {
            if (ColType === kHTMLEditableColType.Number) { return "number"; }
            if (ColType === kHTMLEditableColType.Currency) { return "number"; }
            if (ColType === kHTMLEditableColType.Date) { return "date"; }
            if (ColType === kHTMLEditableColType.DateTime) { return "datetime-local"; }
            if (ColType === kHTMLEditableColType.Time) { return "time"; }
            return "text";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup.ValueInputType");
            return "text";
        }
    };

    public static OperatorLabel = (Operator: kFilterOperator): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
        switch (Operator) {
            case kFilterOperator.Contains: return LNG.FILTERCONTAINS;
            case kFilterOperator.Equals: return LNG.FILTEREQUALS;
            case kFilterOperator.NotEquals: return LNG.FILTERNOTEQUALS;
            case kFilterOperator.StartsWith: return LNG.FILTERSTARTSWITH;
            case kFilterOperator.EndsWith: return LNG.FILTERENDSWITH;
            case kFilterOperator.IsEmpty: return LNG.FILTERISEMPTYOP;
            case kFilterOperator.IsNotEmpty: return LNG.FILTERISNOTEMPTY;
            case kFilterOperator.GreaterThan: return LNG.FILTERGREATERTHAN;
            case kFilterOperator.LessThan: return LNG.FILTERLESSTHAN;
            case kFilterOperator.GreaterOrEqual: return LNG.FILTERGREATEROREQUAL;
            case kFilterOperator.LessOrEqual: return LNG.FILTERLESSOREQUAL;
            case kFilterOperator.Between: return LNG.FILTERBETWEEN;
            case kFilterOperator.Before: return LNG.FILTERBEFORE;
            case kFilterOperator.After: return LNG.FILTERAFTER;
            case kFilterOperator.OnDate: return LNG.FILTERONDATE;
            case kFilterOperator.IsTrue: return LNG.FILTERYES;
            case kFilterOperator.IsFalse: return LNG.FILTERNO;
            default: return LNG.FILTERCONTAINS;
        }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup.OperatorLabel");
            return "";
        }
    };

    public static AddStyles = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (document.getElementById("kFilterPopupStyles") !== null) { return; }
            const Css: string = kColumnFilterPopup._Css();
            kStyles.AddStyles("kFilterPopupStyles", Css);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup.AddStyles");
        }
    };

    private static _Css = (): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
        return `.kFilterPopup{position:absolute;z-index:2147483641;background:#fff;border:1px solid #d2d0ce;`
            + `border-radius:8px;box-shadow:0 8px 28px rgba(0,0,0,0.18);padding:10px;width:260px;`
            + `font-family:'Segoe UI',Tahoma,Helvetica,Arial,sans-serif;font-size:13px;color:#1f1f1f;}`
            + `.kFilterPopupTitle{font-weight:600;margin-bottom:8px;}`
            + `.kFilterOperator,.kFilterValue,.kFilterValuesSearch{width:100%;box-sizing:border-box;`
            + `margin-bottom:6px;padding:4px 6px;border:1px solid #c8c6c4;border-radius:4px;}`
            + `.kFilterChecklist{max-height:180px;overflow-y:auto;border:1px solid #edebe9;border-radius:4px;`
            + `padding:4px;margin-bottom:8px;}`
            + `.kFilterChecklistRow{display:flex;align-items:center;gap:6px;padding:2px 4px;cursor:pointer;}`
            + `.kFilterChecklistRow:hover{background:#f3f2f1;}`
            + `.kFilterSelectAll{font-weight:600;border-bottom:1px solid #edebe9;margin-bottom:2px;}`
            + `.kFilterSwatch{width:14px;height:14px;border-radius:3px;border:1px solid rgba(0,0,0,0.15);display:inline-block;}`
            + `.kFilterButtons{display:flex;justify-content:flex-end;gap:8px;}`
            + `.kFilterBtn{padding:5px 12px;border-radius:4px;border:1px solid #c8c6c4;cursor:pointer;}`
            + `.kFilterApplyBtn{background:#0078d4;color:#fff;border-color:#0078d4;}`
            + `.kFilterClearBtn{background:#fff;color:#1f1f1f;}`;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kColumnFilterPopup._Css");
            return "";
        }
    };
}
